import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const hero = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Highlands Ranch";
const SLUG = "highlands-ranch";
const ZIP = "80129";
const URL = "https://apexav.com/highlands-ranch/home-theater-installation";

const faqs = [
  {
    q: "How much does a home theater installation cost in Highlands Ranch?",
    a: "Highlands Ranch home theater projects typically start around $25,000 for a finished basement build with 4K projection, 7.2.4 immersive surround, motorized screen, and basic acoustic treatment. Backcountry and Eastridge custom estate builds with full Dolby Atmos, tiered seating, starfield ceilings, and acoustic engineering generally run $50,000–$200,000+. We deliver a fixed-bid scope before any construction begins.",
  },
  {
    q: "Can you build a theater in an unfinished Highlands Ranch basement?",
    a: "Yes — this is our most common Highlands Ranch project. We coordinate with your basement finishing contractor (or recommend one) and design the theater into the framing plan from day one: pre-wire, soundproofing, riser placement, equipment closet, HVAC routing. Building it during the basement finish is dramatically cheaper than retrofitting after.",
  },
  {
    q: "How much ceiling height do I need for a Dolby Atmos theater in Highlands Ranch?",
    a: "9-foot finished ceilings are ideal for Atmos height channels and a tiered second row of seating. 8-foot ceilings still support a great 7.2.4 system with low-profile in-ceiling speakers — most Highlands Ranch tract basements work beautifully. For dedicated rooms with 10+ ft ceilings (common in Backcountry custom builds) we can spec 9.4.6 reference-grade systems with full risers.",
  },
  {
    q: "Will the theater system work with my Sonos / Apple TV / cable box?",
    a: "Yes. We design every Highlands Ranch theater to integrate with whatever you already own — Apple TV, Roku, Xfinity X1, NVIDIA Shield, Plex servers, Sonos. The control system gives you one remote and one app instead of five.",
  },
  {
    q: "Do you serve Backcountry, Eastridge, Westridge, and Northridge?",
    a: "Yes — every Highlands Ranch sub-community. Backcountry custom estates get our most ambitious dedicated theater builds (we've done multiple 9.4.6 Atmos rooms with full acoustic engineering). Eastridge, Westridge, and Northridge tract basements are perfect for finished media rooms and 7.2.4 immersive systems.",
  },
];

const testimonials = [
  { quote: "Apex did our Backcountry basement theater — 9.4.6 Atmos, 130\" screen, tiered seating, the whole package. It genuinely sounds better than the local Cinemark.", name: "Jordan B.", location: "Backcountry, Highlands Ranch" },
  { quote: "We finished our Eastridge basement and had Apex pre-wire and install a 7.2.4 theater. From design to first movie night was 10 weeks. Worth every dollar.", name: "Lauren K.", location: "Eastridge" },
  { quote: "Westridge media room — hidden in-wall speakers, 120\" motorized screen, one-touch scenes. Looks like a normal great room until movie night drops.", name: "Mike P.", location: "Westridge" },
];

export const Route = createFileRoute("/highlands-ranch/home-theater-installation")({
  head: () => ({
    meta: [
      { title: "Home Theater Installation Highlands Ranch | Apex AV" },
      { name: "description", content: "Custom home theater design & install in Highlands Ranch — Backcountry, Eastridge, Westridge, Northridge. Dolby Atmos basement theaters from $25K. Free quote." },
      { name: "keywords", content: "home theater installation Highlands Ranch, custom home theater Highlands Ranch, Backcountry home theater, Eastridge media room, Dolby Atmos Highlands Ranch, basement theater Highlands Ranch, 4K projector Highlands Ranch, dedicated theater Highlands Ranch" },
      { property: "og:title", content: "Home Theater Installation Highlands Ranch — Apex AV" },
      { property: "og:description", content: "Custom Dolby Atmos theaters and media rooms designed for Highlands Ranch." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Home Theater Installation Highlands Ranch — Apex AV" },
      { name: "twitter:description", content: "Custom Dolby Atmos theaters and media rooms designed for Highlands Ranch." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Home Theater Installation in ${NEIGHBORHOOD}`,
              description: `Custom home theater design and installation in ${NEIGHBORHOOD}, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, CO ${ZIP}` },
              serviceType: "Custom Home Theater Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "25000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 30000,
              serviceName: "Home Theater Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="home theater installation"
      serviceKey="home-theater"
      parentServiceLabel="home theater"
      parentServiceTo="/services/home-theater"
      h1="Home Theater Installation in Highlands Ranch, CO"
      intro="Custom Dolby Atmos theaters and media rooms designed for Highlands Ranch homes — Backcountry custom estates, Eastridge and Westridge tract basements, Northridge family rooms. Reference-grade surround, 4K projection, motorized screens, and integrated lighting and shading."
      hero={hero}
      bullets={[
        "Designed into your basement finish from day one",
        "7.2.4 and 9.4.6 Dolby Atmos systems",
        "4K and 8K laser projection",
        "Tiered seating, risers, starfield ceilings",
        "One-touch scenes (lights, shades, projector, screen)",
        "Free in-home design consultation",
      ]}
      microNeighborhoods={["Backcountry", "Eastridge", "Westridge", "Northridge", "Southridge", "Highland Heritage"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "Built into your Highlands Ranch basement finish",
          body: (
            <>
              <p>
                The single biggest cost-saver on a Highlands Ranch theater is designing it during your basement finish — not after. When we're in the framing plan from day one, soundproofing, pre-wire, riser placement, equipment closet routing, and HVAC isolation cost a fraction of what they do as a retrofit. We coordinate directly with your basement finishing contractor (or recommend ones we trust) so the theater is engineered, not bolted on.
              </p>
              <p>
                For homeowners with already-finished basements, we still deliver excellent retrofits — we just route low-voltage cabling through accessible chases and use surface-mount or in-ceiling speakers to avoid drywall demo wherever possible.
              </p>
            </>
          ),
        },
        {
          heading: "What a Highlands Ranch theater build includes",
          body: (
            <>
              <p>
                Our standard Highlands Ranch theater spec is a <strong>7.2.4 Dolby Atmos system</strong> with two subwoofers and four overhead height channels, a <strong>4K laser projector</strong> (Sony VPL-XW5000ES or JVC NZ500), a 110–130" motorized screen from Stewart Filmscreen or Screen Innovations, acoustic treatment hidden behind acoustically transparent fabric panels, theater seating with bass shakers, and a unified control remote for one-button scenes (lights dim, shades drop, screen drops, projector fires).
              </p>
              <p>
                For Backcountry custom estates with 10+ foot ceilings we step up to <strong>9.4.6 reference-grade Atmos</strong>, full acoustic engineering (decoupled walls, isolated HVAC), tiered risers with second-row seating, and starfield ceilings.
              </p>
            </>
          ),
        },
        {
          heading: "Integrate with your Highlands Ranch smart home",
          body: (
            <>
              <p>
                Most Highlands Ranch theater clients also want lighting, shading, audio, and security on one app. We design the theater as part of a larger <a href="/highlands-ranch/smart-home-installation" className="text-brand-text underline underline-offset-2">Highlands Ranch smart home installation</a> when that makes sense — same control system, same remote, same app. One number to call when anything needs adjusting.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
